import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FirebaseApp } from 'firebase/app'; // Import the modular interface, not the API!
import { Auth as FirebaseAuth, getAuth } from 'firebase/auth'; // Import the modular interface, not the API!
import firebase from 'firebase/compat/app';
// import 'firebase/compat/auth';
import 'firebase/compat/analytics';
import * as firebaseui from 'firebaseui';
import { AnalyticsService } from '../analytics/analytics.service';
import { EnvironmentService } from '../env';
import { SpinnerService } from '../spinner';
import { UtilityService } from '../util';
import { ExternalAuthState } from './external-auth-state';
import { externalAuthStateEquality } from './external-auth-state-equality';
import { getAuthStateChangedHandler } from './get-auth-state-changed-handler';


interface CompatAppWithDelegate extends firebase.app.App {
	_delegate: FirebaseApp;
}


/**
 * A common authentication service used by all public facing EforAll apps.
 * The service will start automatically once the environment is available.
 */
@Injectable({ providedIn: 'root' })
export class ExternalAuthService {

	private analytics = inject(AnalyticsService);
	private env = inject(EnvironmentService);
	private router = inject(Router);
	private util = inject(UtilityService);
	private spinnerService = inject(SpinnerService);


	public readonly state = signal<ExternalAuthState>(
		{ status: 'pending' },
		{ equal: externalAuthStateEquality },
	);


	public email = computed(() => {

		const authState = this.state();

		if (authState.status !== 'signed-in') return undefined;
		if (authState.user.emailVerified) return authState.user.email ?? undefined;
		else return undefined;
	});


	/**
	 * Returns the domain of the authenticated email address or undefined of not signed in.
	 * The domain is always lower case.
	 */
	public emailDomain = computed(() => {

		const email = this.email();
		if (!email) return undefined;

		return email.toLocaleLowerCase().split('@')[1];
	});


	/**
	 * Returns true if authenticated and auth email ends with eforall.org
	 */
	public isStaffEmail = computed(() => {

		const domain = this.emailDomain();
		if (!domain) return false;
		return domain.endsWith('eforall.org');

	});


	private _compat = signal<{ app: firebase.app.App, auth: firebase.auth.Auth } | undefined>(undefined);

	/**
	 * The 'compat' Firebase App instance from firebase/compat/app
	 * This will consistently contain the app once initializeApp() is called.
	 */
	public compatApp = computed(() => {
		return this._compat()?.app;
	});

	/**
	 * The 'compat' Firebase Auth instance from firebase/compat/app
	 * This will consistently contain the app once initializeApp() is called.
	 */
	public compatAuth = computed(() => {
		return this._compat()?.auth;
	});


	/**
	 * Return the modular FirebaseApp object.
	 * This will consistently contain the app once initializeApp() is called.
	 */
	public app = computed<FirebaseApp | undefined>(() => {
		const compatApp = this.compatApp();
		if (!compatApp) return undefined;
		return (compatApp as CompatAppWithDelegate)._delegate;
	});


	/**
	 * Return the modular Firebase Auth object.
	 * This will consistently contain the app once initializeApp() is called.
	 */
	public auth = computed<FirebaseAuth | undefined>(() => {
		const app = this.app();
		if (!app) return undefined;
		return getAuth(app);
	});


	private _ui: firebaseui.auth.AuthUI | undefined = undefined;


	/**
	 * Service that wraps and monitors the Firebase auth state.
	 */
	constructor() {
		this.setState(this.state());
		this.start();
	}


	private async start() {


		//
		// Wait until the environment is available
		//
		const environment = await this.env.getEnvironmentPromise();


		//
		// Perform Firebase Initialization
		//
		const firebaseConfig = { ...environment.firebaseConfig };
		const domain = document?.location?.hostname?.toLowerCase();
		if (!domain.includes('localhost')) firebaseConfig.authDomain = domain;

		const compatApp = firebase.initializeApp(firebaseConfig);
		const compatAnalytics = firebase.analytics();
		const compatAuth = firebase.auth();

		compatAuth.useDeviceLanguage();

		if (environment.name !== 'LOCALHOST') {
			this.analytics.setAnalytics(compatAnalytics);
		}


		//
		// Listen for changes...
		//
		compatAuth.onAuthStateChanged(

			getAuthStateChangedHandler(
				compatAuth,
				this.analytics,
				this.util,
				this.router,
				this.setState.bind(this),
				environment,
			),
			error => {
				this.analytics.logError(error);
				this.util.log.errorMessage(`${error.message}`);
			}
		);


		this._compat.set({ app: compatApp, auth: compatAuth });

		this._ui = new firebaseui.auth.AuthUI(compatAuth);

	}


	public signOut() {
		const auth = firebase.auth();
		auth.signOut();
		this.analytics.logLogout({ email: auth.currentUser?.email ?? undefined });
	}


	public supportsLocalStorage(): boolean {
		let hasWebStorage = false;

		try {
			const test = 'test-for-support';
			localStorage.setItem(test, test)
			hasWebStorage = localStorage.getItem(test) === test;
			localStorage.removeItem(test);
		}
		catch (e) { hasWebStorage = false; }

		return hasWebStorage;
	}


	/**
	 * Get the firebase id token to send along with the payload to functions
	 */
	public async getIdToken(): Promise<string> {

		let attempt = 0;
		let auth = this.state();

		//
		// Try for 10 seconds  (100 * 100ms)
		// Consider: Should this just keep looping until signed in?
		//
		while (attempt++ < 100) {

			if (auth.status == 'signed-in') {
				return await auth.user.getIdToken();
			}

			await this.util.setTimeout(100);	// 100ms
			auth = this.state();
		}

		throw new Error(`getIdToken() failed because the auth state is ${auth.status}`);
	}


	/**
	 * Sets the state signal and triggers the spinner and data loading
	 */
	private setState(state: ExternalAuthState) {

		this.state.set(state);
		const status = state.status;

		if (status == 'pending') {
			this.spinnerService.addSpinner('auth-pending', 'Signing In');
		}
		else {
			this.spinnerService.removeSpinner('auth-pending');
		}
	}


	public async render(method: "Google" | "Password") {

		const environment = await this.env.getEnvironmentPromise();

		const provider = method == 'Google'
			? {
				provider: firebase.auth.GoogleAuthProvider.PROVIDER_ID,
				customParameters: {
					prompt: 'select_account' // Without this user cannot change to a different account
				},
				scopes: []
			}
			: firebase.auth.EmailAuthProvider.PROVIDER_ID;

		const signInFlow = environment.name == 'LOCALHOST' ? 'popup' : 'redirect';

		const uiConfig: firebaseui.auth.Config = {
			callbacks: {
				signInSuccessWithAuthResult: (authResult: any, redirectUrl: any) => {
					console.log('signInSuccessWithAuthResult', authResult, redirectUrl);
					return false;
				},
			},
			signInSuccessUrl: window.location.href,
			signInFlow,
			signInOptions: [provider],
			// tosUrl: 'https://eforall.org/terms-of-use/',
			privacyPolicyUrl: 'https://eforall.org/privacy-policy/',
		};


		if (this._ui) {
			// this._ui.reset();

			console.log('ui.start with ' + method);
			this._ui.start('#firebaseui-auth-container', uiConfig);
		}

		else {
			console.error('FirebaseUI not initialized');
		}
	}


}